import type { Duration } from "src/utils/date";

/**
 * ==================
 * STATE SHAPE
 * */
export type SearchState = { orgId: string } & Partial<{
  select: string[];
  search: string;
  columns?: string[];
  timezone?: string;
  duration: Duration | "";
  startDate: string;
  endDate: string;
  filters: Condition[];
  groupBys: Field[];
  aggregations: Aggregation[];
  formulas: ColumnFormula[];
  having: ColumnHaving[];
  compareWith?: {
    ago: Duration | "default";
    method: "delta" | "tuple";
  };
  format?: "json" | "csv";
}>;

export type SearchWindow = Partial<{
  limit: number;
  columns?: string[];
  timezone?: string;
  order: {
    by: Aggregation | Field | string;
    direction: "ascending" | "descending";
  }[];
}>;

export type Field = {
  target: string;
  attribute: string;
  operator?: string;
  measureKey?: string; //TODO fix this hack used for pivot tables.. Should create a new type for this
};

export type Aggregation = Field & {
  reducer: string;
  filters?: Condition[];
};

export type Condition = Field & {
  key: string;
  comparison: Comparator;
};

export type Formula = [string, ...(string | Formula)[]];

export type ColumnFormula = {
  name: string;
  formula: Formula;
};

export type ColumnHaving = {
  column: string;
} & {
  comparison: Comparator;
};

export type Comparator =
  | { IN: string[] }
  | { "NOT IN": string[] }
  | { WITHIN: string[] };

/**
 * ==================
 * ACTIONS/METHODS
 * */
export enum SearchActions {
  loadState,
  reset,
  resetDate,
  setAggregations,
  setGroupBys,
  setSearch,
  setDates,
  setDuration,
  setCompareWith,
  setFilters,
  setHaving,
  setSelection,
}

type ActionType<Type, Value> = { type: Type; value: Value };
export type SearchAction =
  | { type: SearchActions.reset }
  | { type: SearchActions.resetDate }
  | ActionType<SearchActions.loadState, string>
  | ActionType<SearchActions.setAggregations, SearchState["aggregations"]>
  | ActionType<
      SearchActions.setDates,
      [SearchState["startDate"], SearchState["endDate"]]
    >
  | ActionType<SearchActions.setDuration, SearchState["duration"]>
  | ActionType<SearchActions.setFilters, SearchState["filters"]>
  | ActionType<SearchActions.setGroupBys, SearchState["groupBys"]>
  | ActionType<SearchActions.setSearch, SearchState["search"]>
  | ActionType<SearchActions.setSelection, SearchState["select"]>
  | ActionType<SearchActions.setCompareWith, SearchState["compareWith"]>
  | ActionType<SearchActions.setHaving, SearchState["having"]>;
